import { SkipLink } from '@today/ui-components'
import { type ReactElement, type ReactNode, useId } from 'react'

/**
 * Props for {@link PlaneShellFrame}.
 *
 * @property sidebar - The plane sidebar region (e.g. an `AppSidebar`).
 * @property header - The plane header region (e.g. an `AppHeader`).
 * @property band - The context-band slot (e.g. a `ContextBand`), rendered above the
 *   content when a viewer is narrowing into a lower plane (AD-4). Omitted → no band.
 * @property toasts - The toast-container slot, pinned to the viewport corner.
 * @property children - The routed plane content (the consumer's `<Outlet/>`).
 * @property skipToContentLabel - Accessible text of the skip link; defaults to
 *   `'Skip to content'`.
 */
export interface PlaneShellFrameProps {
	sidebar: ReactNode
	header: ReactNode
	band?: ReactNode
	toasts?: ReactNode
	children: ReactNode
	skipToContentLabel?: string
}

/**
 * The generic per-plane shell frame (AD-4): a skip-to-content link, a sidebar
 * region, a header region, the routed content, and a toast-container slot.
 *
 * @remarks
 * - **Holds no plane data.** Sidebar, header, band, toasts and content all arrive
 *   as props; the frame owns only the skip link, the layout grid and the `<main>`
 *   landmark. The three per-plane shells (Wave 2) bind their own data over it.
 * - **Context band** (AD-4) renders above the content, below the header, only when
 *   the consumer supplies one (a viewer narrowing into a lower plane).
 * - **Skip-to-content** is the first focusable element and targets the `<main>`
 *   region, so a keyboard user can bypass the chrome (ux-interaction-standards).
 * - **Brand-agnostic:** every class is a `ds-*` token utility; any transition
 *   uses `ds-*` motion tokens and honours `prefers-reduced-motion`.
 *
 * `ShellFrame` in the frontend-architecture doc is an alias for this component.
 */
export function PlaneShellFrame({
	sidebar,
	header,
	band,
	toasts,
	children,
	skipToContentLabel = 'Skip to content',
}: PlaneShellFrameProps): ReactElement {
	const mainId = useId()
	return (
		<div className="flex min-h-screen flex-col">
			<SkipLink targetId={mainId} label={skipToContentLabel} />
			<div className="flex flex-1">
				<aside className="shrink-0 border-r border-ds-border bg-ds-surface p-ds-3">{sidebar}</aside>
				<div className="flex min-w-0 flex-1 flex-col">
					{header}
					{band}
					<main id={mainId} className="min-w-0 flex-1 px-ds-gutter py-ds-6">
						{children}
					</main>
				</div>
			</div>
			{toasts ? <div className="fixed bottom-ds-4 right-ds-4 z-10">{toasts}</div> : null}
		</div>
	)
}
